import React, { useState, useRef } from 'react';
import { X, ExternalLink, MapPin, Calendar, Building2, ShieldCheck, Pencil, LogIn, Loader2, AlertCircle, Image as ImageIcon, Trash2, Linkedin } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';
import { claimStartup, updateStartup } from '../services/api';
import { linkedinCompanyUrl } from '../lib/linkedin';

// Resize a File client-side to a max 256px square, return a base64 data URL.
const readAndResize = (file, maxSize = 256) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL('image/png'));
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const EditForm = ({ startup, onSave, onCancel, saving, error }) => {
  const [form, setForm] = useState({
    description: startup.description || '',
    website: startup.website || '',
    sector: startup.sector || '',
    stage: startup.stage || '',
    badge: startup.badge || '',
    address: startup.address || '',
  });
  const [logoDataUrl, setLogoDataUrl] = useState(startup.logo_url || '');
  const [foundersText, setFoundersText] = useState(
    (startup.founders || []).map((f) => (f.linkedin ? `${f.name} | ${f.linkedin}` : f.name)).join('\n')
  );
  const [investorsText, setInvestorsText] = useState((startup.investors || []).join(', '));
  const [logoError, setLogoError] = useState(null);
  const fileRef = useRef(null);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError(null);
    if (!file.type.startsWith('image/')) {
      setLogoError('Please pick an image file.');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setLogoError('Image is too large (max 3 MB).');
      return;
    }
    try {
      const dataUrl = await readAndResize(file, 256);
      setLogoDataUrl(dataUrl);
    } catch (err) {
      setLogoError('Could not read that image, try another one.');
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const founders = foundersText
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .map((l) => {
            const [name, linkedin] = l.split('|').map((x) => (x || '').trim());
            return { name: name || '', linkedin: linkedin || '' };
          })
          .filter((f) => f.name);
        const investors = investorsText.split(',').map((x) => x.trim()).filter(Boolean);
        onSave({ ...form, founders, investors, logo_url: logoDataUrl });
      }}
      className="p-4 space-y-2.5"
    >
      {/* Logo upload */}
      <div>
        <span className="block text-[11px] font-medium text-slate-600 mb-0.5">Logo</span>
        <div className="flex items-center gap-3">
          <div
            onClick={() => fileRef.current && fileRef.current.click()}
            className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-200 hover:border-slate-400 bg-slate-50 flex items-center justify-center flex-shrink-0 cursor-pointer overflow-hidden"
          >
            {logoDataUrl ? (
              <img src={logoDataUrl} alt="logo preview" className="w-full h-full object-contain p-1" />
            ) : (
              <ImageIcon size={20} className="text-slate-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="edit-logo-upload-btn"
                onClick={() => fileRef.current && fileRef.current.click()}
                className="text-[12px] font-medium bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md"
              >
                {logoDataUrl ? 'Change' : 'Upload'}
              </button>
              {logoDataUrl && (
                <button
                  type="button"
                  data-testid="edit-logo-remove-btn"
                  onClick={() => setLogoDataUrl('')}
                  className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-rose-600"
                >
                  <Trash2 size={12} /> Remove
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">PNG or JPG, resized to 256px on your device.</p>
            {logoError && <p className="text-[11px] text-rose-600 mt-0.5">{logoError}</p>}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid="edit-logo-file-input"
            className="hidden"
            onChange={handleLogoPick}
          />
        </div>
      </div>

      <Field label="Description" textarea value={form.description} onChange={change('description')} />
      <Field label="Website" value={form.website} onChange={change('website')} />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Sector" value={form.sector} onChange={change('sector')} />
        <Field label="Stage" value={form.stage} onChange={change('stage')} />
      </div>
      <Field label="Badge" value={form.badge} onChange={change('badge')} />
      <Field label="Address" value={form.address} onChange={change('address')} />

      <label className="block">
        <span className="block text-[11px] font-medium text-slate-600 mb-0.5">
          Founders <span className="text-slate-400">(one per line: Name | LinkedIn URL)</span>
        </span>
        <textarea
          value={foundersText}
          onChange={(e) => setFoundersText(e.target.value)}
          rows={3}
          data-testid="edit-founders"
          placeholder="Jane Doe | https://linkedin.com/in/janedoe"
          className="w-full text-[13px] px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 resize-none"
        />
      </label>
      <label className="block">
        <span className="block text-[11px] font-medium text-slate-600 mb-0.5">
          Investors <span className="text-slate-400">(comma-separated)</span>
        </span>
        <input
          value={investorsText}
          onChange={(e) => setInvestorsText(e.target.value)}
          data-testid="edit-investors"
          placeholder="Sequoia, Y Combinator"
          className="w-full text-[13px] px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
        />
      </label>

      {error && (
        <div className="flex items-start gap-2 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-2">
          <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          data-testid="edit-save-btn"
          className="flex-1 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-medium text-[13px] py-2 rounded-lg"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] text-slate-500 hover:text-slate-900 px-3 py-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const Field = ({ label, textarea, ...props }) => (
  <label className="block">
    <span className="block text-[11px] font-medium text-slate-600 mb-0.5">{label}</span>
    {textarea ? (
      <textarea
        {...props}
        rows={3}
        className="w-full text-[13px] px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 resize-none"
      />
    ) : (
      <input
        {...props}
        className="w-full text-[13px] px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
      />
    )}
  </label>
);

const StartupDetail = ({ startup, onClose, onUpdated }) => {
  const { user, login } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimMsg, setClaimMsg] = useState(null);

  if (!startup) return null;

  const isOwner = user && startup.claimed_by === user.user_id;
  const isClaimed = !!startup.claimed_by;

  const handleClaim = async () => {
    if (!user) { login(); return; }
    setClaiming(true); setClaimMsg(null);
    try {
      await claimStartup(startup.id);
      if (onUpdated) await onUpdated();
      setClaimMsg({ type: 'ok', text: 'Claimed! You can now edit your startup.' });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not claim this startup.';
      setClaimMsg({ type: 'err', text: msg });
    } finally {
      setClaiming(false);
    }
  };

  const handleSave = async (patch) => {
    setSaving(true); setError(null);
    try {
      await updateStartup(startup.id, patch);
      if (onUpdated) await onUpdated();
      setEditing(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save changes.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[950] w-full bg-white rounded-t-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200 max-h-[80vh] flex flex-col sm:absolute sm:inset-x-auto sm:bottom-auto sm:top-32 sm:right-4 sm:w-[360px] sm:rounded-2xl sm:slide-in-from-right-2 sm:max-h-[calc(100vh-200px)]" data-testid="startup-detail-panel">
      <div className="sm:hidden flex justify-center pt-2.5 pb-0.5 flex-shrink-0">
        <div className="h-1.5 w-10 rounded-full bg-slate-300" />
      </div>
      <div className="p-4 pt-2 sm:pt-4 flex-shrink-0">
        <div className="flex items-start gap-3">
          <Logo startup={startup} size={48} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold text-slate-900 text-[16px] truncate">{startup.name}</h3>
              {startup.yc && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-orange-500 text-white text-[10px] font-bold">Y</span>
              )}
              {isClaimed && (
                <span title="Claimed by owner" className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <ShieldCheck size={10} /> Claimed
                </span>
              )}
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">{startup.badge}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="startup-detail-close"
            className="text-slate-400 hover:text-slate-700 -mt-1 -mr-1 p-1 rounded-lg hover:bg-slate-100"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {editing ? (
          <EditForm
            startup={startup}
            onSave={handleSave}
            onCancel={() => { setEditing(false); setError(null); }}
            saving={saving}
            error={error}
          />
        ) : (
          <div className="px-4 pb-4">
            <p className="text-[13px] text-slate-700 leading-relaxed">{startup.description}</p>

            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="inline-flex items-center text-[11px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200">{startup.sector}</span>
              <span className="inline-flex items-center text-[11px] font-medium px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">{startup.neighborhood}</span>
              {startup.events && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
                  <Calendar size={11} /> Event this week
                </span>
              )}
            </div>

            <div className="mt-3 space-y-1.5">
              <div className="flex items-center gap-2 text-[12.5px] text-slate-600">
                <MapPin size={13} className="mt-0.5 flex-shrink-0 text-rose-500" fill="currentColor" />
                <span>{startup.address}</span>
              </div>
              {startup.founded ? (
                <div className="flex items-center gap-2 text-[12.5px] text-slate-600">
                  <Building2 size={13} className="text-slate-400" />
                  <span>Founded {startup.founded}</span>
                </div>
              ) : null}
            </div>

            {startup.website ? (
              <a
                href={`https://${(startup.website || '').replace(/^https?:\/\//, '')}`}
                target="_blank"
                rel="noreferrer"
                data-testid="startup-website-link"
                className="mt-3.5 inline-flex items-center justify-center gap-1.5 w-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium py-2 rounded-lg transition-colors"
              >
                Visit website
                <ExternalLink size={13} />
              </a>
            ) : null}

            {linkedinCompanyUrl(startup) ? (
              <a
                href={linkedinCompanyUrl(startup)}
                target="_blank"
                rel="noreferrer"
                data-testid="startup-linkedin-link"
                className="mt-2 inline-flex items-center justify-center gap-1.5 w-full bg-[#0A66C2] hover:bg-[#004182] text-white text-[13px] font-medium py-2 rounded-lg transition-colors"
              >
                <Linkedin size={14} />
                Company on LinkedIn
              </a>
            ) : null}

            {/* Claim / Edit actions */}
            <div className="mt-2">
              {isOwner ? (
                <button
                  onClick={() => setEditing(true)}
                  data-testid="edit-startup-btn"
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-[13px] font-medium py-2 rounded-lg transition-colors"
                >
                  <Pencil size={13} />
                  Edit your startup
                </button>
              ) : !isClaimed ? (
                <button
                  onClick={handleClaim}
                  disabled={claiming}
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-[13px] font-medium py-2 rounded-lg transition-colors"
                >
                  {claiming
                    ? <Loader2 size={13} className="animate-spin" />
                    : (user ? <ShieldCheck size={13} /> : <LogIn size={13} />)}
                  {user ? 'Claim your startup' : 'Sign in to claim'}
                </button>
              ) : null}
              {claimMsg && (
                <div className={`mt-2 text-[12px] px-2.5 py-2 rounded-lg border flex items-start gap-2 ${
                  claimMsg.type === 'ok'
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : 'bg-rose-50 text-rose-700 border-rose-200'
                }`}>
                  {claimMsg.type === 'ok'
                    ? <ShieldCheck size={13} className="mt-0.5 flex-shrink-0" />
                    : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                  <span>{claimMsg.text}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartupDetail;
